/// <reference types="gtag.js" />
import Clarity from '@microsoft/clarity'

type Value = string | number | boolean | null | undefined

export type AnalyticsConfig = {
  /** Omit to leave GA4 off. */
  ga4?: { measurementId: string }
  /** Omit to leave Clarity off. */
  clarity?: { projectId: string }
}

type AnalyticsUser = {
  /** A pseudonymous id, such as the B2C subject. Sent to both vendors, so never an email. */
  id: string
  /** Clarity only, shown against the session in its dashboard. */
  friendlyName?: string
  /** GA4's terms forbid PII, so nothing here may identify a person. */
  ga4Properties?: Record<string, Value>
  clarityTags?: Record<string, Value>
}

export type Analytics = {
  /** Loads the vendor scripts. Call once, outside React, before the first render. */
  init(): void
  identify(user: AnalyticsUser): void
  page(page: { path: string; title?: string }): void
  event(name: string, params?: Record<string, Value>): void
}

const GA4_SCRIPT_ID = 'ga4-script'

declare global {
  interface Window {
    dataLayer?: unknown[]
    clarity?: unknown
  }
}

function attempt(fn: () => void) {
  try {
    fn()
  } catch {
    // Analytics is never worth a broken page.
  }
}

function present(properties: Record<string, Value>) {
  return Object.fromEntries(
    Object.entries(properties).filter(
      ([, value]) => value !== null && value !== undefined && value !== '',
    ),
  )
}

function asStrings(properties: Record<string, Value>) {
  return Object.fromEntries(
    Object.entries(present(properties)).map(([key, value]) => [key, String(value)]),
  )
}

// Record ids are replaced so `/cma/4821/report` reports as `/cma/:id/report`.
function routePattern(pathname: string): string {
  return pathname.replace(/\/\d+(?=\/|$)/g, '/:id')
}

// A local copy of Google's global `gtag`, typed by @types/gtag.js, so a call works before the
// script defines the global. It must be a `function`: gtag.js only reads Arguments objects off
// the queue, not arrays.
const gtag: Gtag.Gtag = function () {
  // biome-ignore lint/complexity/noArguments: see above.
  window.dataLayer?.push(arguments)
}

type Tracker = {
  live(): boolean
  init(): void
  identify(user: AnalyticsUser): void
  page(page: { path: string; location: string; title?: string }, user: AnalyticsUser | null): void
  event(name: string, params: Record<string, Value>): void
}

function ga4Tracker(measurementId: string): Tracker {
  return {
    live: () => !!window.dataLayer,

    // Google publishes no npm package for gtag.js, so this is their documented snippet. It
    // reuses an existing queue rather than replacing it, in case a GTM container or other tag
    // is added to the page, and self-guards on the script id the way Clarity's init does.
    init() {
      if (document.getElementById(GA4_SCRIPT_ID)) return

      window.dataLayer = window.dataLayer || []
      gtag('js', new Date())
      // Page views are sent by hand so each hit carries the route pattern instead of a URL
      // with a record id in it; GA4's own history-based page views would double-count.
      gtag('config', measurementId, { send_page_view: false })

      const tag = document.createElement('script')
      tag.async = true
      tag.id = GA4_SCRIPT_ID
      tag.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
      document.head.appendChild(tag)
    },

    identify(user) {
      gtag('set', { user_id: user.id })
      if (user.ga4Properties) gtag('set', 'user_properties', asStrings(user.ga4Properties))
    },

    page(page) {
      gtag('event', 'page_view', { page_location: page.location, page_title: page.title })
    },

    // Unlike user properties these keep their types, so a count can be registered as a
    // metric rather than only as a text dimension.
    event(name, params) {
      gtag('event', name, present(params))
    },
  }
}

function clarityTracker(projectId: string): Tracker {
  return {
    live: () => typeof window.clarity === 'function',
    init: () => Clarity.init(projectId),

    identify(user) {
      for (const [key, value] of Object.entries(asStrings(user.clarityTags ?? {}))) {
        Clarity.setTag(key, value)
      }
    },

    // Clarity ties its custom id to a page, so it is sent with every page view.
    page(page, user) {
      if (!user) return
      Clarity.identify(user.id, undefined, page.path, user.friendlyName)
    },

    event(name) {
      Clarity.event(name)
    },
  }
}

/**
 * GA4 and Microsoft Clarity behind one interface. A vendor missing from `config` is off, and
 * no call ever throws. Property and tag names are passed through in each vendor's own casing:
 * saved Clarity filters and registered GA4 custom dimensions reference them.
 */
export function createAnalytics(config: AnalyticsConfig): Analytics {
  const trackers = [
    config.ga4 && ga4Tracker(config.ga4.measurementId),
    config.clarity && clarityTracker(config.clarity.projectId),
  ].filter((tracker): tracker is Tracker => !!tracker)

  let user: AnalyticsUser | null = null

  // `live` guards against calls before init, or after a blocker stopped the script loading.
  function each(fn: (tracker: Tracker) => void) {
    for (const tracker of trackers) {
      if (tracker.live()) attempt(() => fn(tracker))
    }
  }

  return {
    init() {
      for (const tracker of trackers) attempt(() => tracker.init())
    },

    identify(next) {
      user = next
      each((tracker) => tracker.identify(next))
    },

    page({ path, title }) {
      const pattern = routePattern(path)
      const url = new URL(window.location.href)
      url.pathname = pattern
      each((tracker) => tracker.page({ path: pattern, location: url.href, title }, user))
    },

    event(name, params = {}) {
      each((tracker) => tracker.event(name, params))
    },
  }
}
